import { Checkbox, Combobox, Label, makeStyles, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHiddenComboboxCheckIconStyles } from '../../hooks/useHiddenComboboxCheckIconStyles';
import { ShowableTaxReturnItemRecord } from '../add-return/AddReturnPanel';
import { LISTBOX_MAX_HEIGHT } from '../common/VirtualizedOptionList';

export interface IEditReturnAdvancedEntitySectionProps {
    taxReturnData: ShowableTaxReturnItemRecord | undefined;
    customGroups: any;
    customRanges: any;
    onChangeCheckbox: (data: any, key: string) => void;
    onChangeDropdown: (data: any, key: string) => void;
    hasEditTaxReturnsDataEnabled?: boolean;
}

const useStyles = makeStyles({
    field: {
        marginBottom: '10px',
    },
    label: {
        lineHeight: 2,
    },
    combobox: {
        width: '312px',
        minWidth: '312px',
    },
    listbox: {
        maxHeight: `${LISTBOX_MAX_HEIGHT}px !important`,
        overflowY: 'auto',
    },
});

const EditReturnAdvancedEntitySection: React.FC<IEditReturnAdvancedEntitySectionProps> = ({
    taxReturnData,
    customGroups,
    customRanges,
    onChangeCheckbox,
    onChangeDropdown,
    hasEditTaxReturnsDataEnabled = true,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { hidden: hiddenCheckIcon } = useHiddenComboboxCheckIconStyles();

    const [customGroupFilter, setCustomGroupFilter] = useState('');
    const [customRangeFilter, setCustomRangeFilter] = useState('');

    const selectedCustomGroup = customGroups?.find(
        (group: any) => group.customGroupKey?.toString() === taxReturnData?.columnFileKey?.toString()
    );
    const selectedCustomRange = customRanges?.find((range: any) => range.rangeKey?.toString() === taxReturnData?.rangeFileKey?.toString());

    const filteredCustomGroups =
        customGroups?.filter((group: any) => {
            const query = customGroupFilter.toLowerCase();
            if (!query) {
                return true;
            }
            return (
                group.name?.toLowerCase().includes(query) ||
                group.description?.toLowerCase().includes(query) ||
                group.nameAndDescription?.toLowerCase().includes(query)
            );
        }) ?? [];

    const filteredCustomRanges =
        customRanges?.filter((range: any) => {
            const query = customRangeFilter.toLowerCase();
            if (!query) {
                return true;
            }
            return (
                range.name?.toLowerCase().includes(query) ||
                range.description?.toLowerCase().includes(query) ||
                range.nameAndDescription?.toLowerCase().includes(query)
            );
        }) ?? [];

    const handleCustomGroupSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
        if (!data.optionValue) {
            onChangeDropdown(undefined, 'columnFileKey');
            setCustomGroupFilter('');
            return;
        }
        onChangeDropdown(parseInt(data.optionValue, 10), 'columnFileKey');
        setCustomGroupFilter('');
    };

    const handleCustomRangeSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
        if (!data.optionValue) {
            onChangeDropdown(undefined, 'rangeFileKey');
            setCustomRangeFilter('');
            return;
        }
        onChangeDropdown(parseInt(data.optionValue, 10), 'rangeFileKey');
        setCustomRangeFilter('');
    };

    return (
        <>
            <div className={styles.field}>
                <Label className={styles.label}>{t('customGroup').toString()}</Label>
                <Combobox
                    id='customGroup'
                    className={styles.combobox}
                    placeholder={t('selectCustomGroup').toString()}
                    value={customGroupFilter || selectedCustomGroup?.nameAndDescription || selectedCustomGroup?.name || ''}
                    selectedOptions={taxReturnData?.columnFileKey != null ? [String(taxReturnData.columnFileKey)] : []}
                    onOptionSelect={handleCustomGroupSelect}
                    onChange={(event) => setCustomGroupFilter(event.target.value)}
                    freeform
                    clearable
                    disabled={!hasEditTaxReturnsDataEnabled}
                    listbox={{ className: styles.listbox }}
                >
                    {filteredCustomGroups.length > 0 ? (
                        filteredCustomGroups.map((group: any) => (
                            <Option
                                key={String(group.customGroupKey)}
                                className={hiddenCheckIcon}
                                value={String(group.customGroupKey)}
                                text={group.nameAndDescription ?? group.name ?? ''}
                            >
                                {group.name}
                            </Option>
                        ))
                    ) : (
                        <Option
                            disabled
                            className={hiddenCheckIcon}
                            text={
                                customGroupFilter
                                    ? t('noOptionsFound', { searchText: customGroupFilter }).toString()
                                    : t('noRecordsToDisplay').toString()
                            }
                        >
                            {customGroupFilter ? t('noOptionsFound', { searchText: customGroupFilter }) : t('noRecordsToDisplay')}
                        </Option>
                    )}
                </Combobox>
            </div>

            <Checkbox
                checked={taxReturnData?.ignoreEntityGroup}
                onChange={(_, checked) => onChangeCheckbox(checked, 'ignoreEntityGroup')}
                label={t('useGroupOnly').toString()}
                disabled={!hasEditTaxReturnsDataEnabled}
            />
            <Checkbox
                checked={taxReturnData?.includeAllBU}
                data-testid='includeAllBuCheckbox'
                onChange={(_, checked) => onChangeCheckbox(checked, 'includeAllBU')}
                label={t('includeAllBu').toString()}
                disabled={!hasEditTaxReturnsDataEnabled}
            />

            <div className={styles.field}>
                <Label className={styles.label}>{t('customRange').toString()}</Label>
                <Combobox
                    id='customRanges'
                    className={styles.combobox}
                    placeholder={t('selectCustomRange').toString()}
                    value={customRangeFilter || selectedCustomRange?.nameAndDescription || selectedCustomRange?.name || ''}
                    selectedOptions={taxReturnData?.rangeFileKey != null ? [String(taxReturnData.rangeFileKey)] : []}
                    onOptionSelect={handleCustomRangeSelect}
                    onChange={(event) => setCustomRangeFilter(event.target.value)}
                    freeform
                    clearable
                    disabled={!hasEditTaxReturnsDataEnabled}
                    listbox={{ className: styles.listbox }}
                >
                    {filteredCustomRanges.length > 0 ? (
                        filteredCustomRanges.map((range: any) => (
                            <Option
                                key={String(range.rangeKey)}
                                className={hiddenCheckIcon}
                                value={String(range.rangeKey)}
                                text={range.nameAndDescription ?? range.name ?? ''}
                            >
                                {range.name}
                            </Option>
                        ))
                    ) : (
                        <Option
                            disabled
                            className={hiddenCheckIcon}
                            text={
                                customRangeFilter
                                    ? t('noOptionsFound', { searchText: customRangeFilter }).toString()
                                    : t('noRecordsToDisplay').toString()
                            }
                        >
                            {customRangeFilter ? t('noOptionsFound', { searchText: customRangeFilter }) : t('noRecordsToDisplay')}
                        </Option>
                    )}
                </Combobox>
            </div>
        </>
    );
};

export default EditReturnAdvancedEntitySection;
